import { ICellRendererParams } from "ag-grid-enterprise";
import { ChevronDown, ChevronRight } from "lucide-react";
import React, { useEffect, useState } from "react";

import { IgTooltip, IgTypography } from "@intelligems/ig-theme";
import { ShopifyProductId } from "@intelligems/ig-utils";
import { Box, Stack, useTheme } from "@mui/material";
import { useGetOrg } from "@src/api/organizations/get-organization";
import { ProductListImage } from "@src/components/MainApp/SharedProductPicker/list/ProductListImage";
import { ProductPickerSelectedProduct } from "@src/store/slices/productPicker/types";
import { openShopifyProduct } from "@src/utils/shopify";

export const ProductListImageAndText = (
  params: ICellRendererParams & {
    data: ProductPickerSelectedProduct;
  }
) => {
  const { data, context, node } = params;
  const org = useGetOrg();
  const theme = useTheme();
  const [isExpanded, setIsExpanded] = useState(node?.expanded ?? false);

  useEffect(() => {
    setIsExpanded(node?.expanded ?? false);
  }, [node?.expanded]);

  const hasPartialVariantSelection =
    data?.selectedVariantIds &&
    data.selectedVariantIds.length > 0 &&
    data.selectedVariantIds.length < (data.totalVariantCount ?? 0);
  const parentProduct = context?.parentProduct;
  const displayId = data?.id ? new ShopifyProductId(data.id).id : "";
  const linkId = parentProduct?.id
    ? new ShopifyProductId(parentProduct.id).id
    : displayId;
  const hasMultipleVariants = data?.variants && data.variants.length > 1;

  const onIdClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (org.data.org.shopId) {
      openShopifyProduct(org.data.org.shopId, linkId);
    }
  };

  const handleRowClick = () => {
    if (hasMultipleVariants && node?.master) {
      const newExpandedState = !node.expanded;
      node.setExpanded(newExpandedState);
      setIsExpanded(newExpandedState);
    }
  };

  return (
    <Stack
      direction="row"
      spacing={3}
      alignItems="center"
      onClick={handleRowClick}
      sx={{
        height: "100%",
        cursor: hasMultipleVariants ? "pointer" : "default",
      }}
    >
      <Box sx={{ width: "40px", flexShrink: 0 }}>
        <ProductListImage {...params} />
      </Box>

      <Stack
        direction="column"
        justifyContent="center"
        alignItems="flex-start"
        flex={1}
        minWidth={0}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ width: "100%", minWidth: 0 }}
        >
          <IgTypography
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              minWidth: 0,
            }}
          >
            {data?.title}
          </IgTypography>
          {hasMultipleVariants && node?.master && (
            <Stack
              sx={{
                flexShrink: 0,
                alignItems: "center",
                color: theme.palette.text.secondary,
              }}
            >
              {isExpanded ? (
                <ChevronDown size={16} />
              ) : (
                <ChevronRight size={16} />
              )}
            </Stack>
          )}
        </Stack>
        <IgTooltip title={"Go to this product in Shopify"}>
          <IgTypography
            sx={{
              "&:hover": {
                cursor: "pointer",
                textDecoration: "underline",
              },
            }}
            variant={"body2"}
            color={theme.palette.text.secondary}
            onClick={onIdClick}
          >
            {hasPartialVariantSelection
              ? `${data.selectedVariantIds!.length} of ${data.totalVariantCount} variants selected`
              : displayId}
          </IgTypography>
        </IgTooltip>
      </Stack>
    </Stack>
  );
};
